import React from 'react';

import type {DateTime} from '@gravity-ui/date-utils';
import {useControlledState, useLang} from '@gravity-ui/uikit';

import {useBaseDateFieldState} from '../../DateField';
import type {DateFieldState} from '../../DateField';
import type {DateFieldSectionType, DateFieldSectionWithoutPosition} from '../../DateField/types';
import type {EDITABLE_SEGMENTS} from '../../DateField/utils';
import {
    addSegment,
    adjustDateToFormat,
    getFormatInfo,
    isAllSegmentsValid,
    markValidSection,
    parseDateFromString,
    setSegment,
    useFormatSections,
} from '../../DateField/utils';
import type {DateFieldBase} from '../../types/datePicker';
import type {RangeValue} from '../../types/inputs';
import {isInvalid} from '../../utils/dates';
import {useDefaultTimeZone} from '../../utils/useDefaultTimeZone';
import {createPlaceholderRangeValue, getRangeEditableSections, isValidRange} from '../utils';

export interface RangeDateFieldStateOptions extends DateFieldBase<RangeValue<DateTime>> {
    delimiter?: string;
}

const RANGE_FORMAT_DELIMITER = ' — ';

export type RangeDateFieldState = DateFieldState<RangeValue<DateTime>>;

export function useRangeDateFieldState(props: RangeDateFieldStateOptions): RangeDateFieldState {
    const [value, setRange] = useControlledState(
        props.value,
        props.defaultValue || null,
        props.onUpdate,
    );

    const inputTimeZone = useDefaultTimeZone(
        props.value?.start || props.defaultValue?.start || props.placeholderValue,
    );
    const timeZone = props.timeZone || inputTimeZone;

    const handleUpdateRange = (v: RangeValue<DateTime> | null) => {
        setRange(
            v ? {start: v.start.timeZone(inputTimeZone), end: v.end.timeZone(inputTimeZone)} : v,
        );
    };

    const [placeholderDate, setPlaceholderDate] = React.useState<RangeValue<DateTime>>(() => {
        return createPlaceholderRangeValue({
            placeholderValue: props.placeholderValue,
            timeZone,
        });
    });

    const format = props.format || 'L';
    const delimiter = props.delimiter ?? RANGE_FORMAT_DELIMITER;
    const sections = useFormatSections(format);
    const formatInfo = React.useMemo(() => getFormatInfo(sections), [sections]);

    const allSegments = formatInfo.availableUnits;

    // eslint-disable-next-line prefer-const
    let [validSegments, setValidSegments] = React.useState<RangeValue<typeof EDITABLE_SEGMENTS>>(
        () => (value ? {start: {...allSegments}, end: {...allSegments}} : {start: {}, end: {}}),
    );

    if (
        value &&
        (!isAllSegmentsValid(allSegments, validSegments.start) ||
            !isAllSegmentsValid(allSegments, validSegments.end))
    ) {
        setValidSegments({start: {...allSegments}, end: {...allSegments}});
    }

    if (
        !value &&
        Object.keys(allSegments).length > 0 &&
        isAllSegmentsValid(allSegments, validSegments.start) &&
        Object.keys(validSegments.start).length === Object.keys(allSegments).length &&
        isAllSegmentsValid(allSegments, validSegments.end) &&
        Object.keys(validSegments.end).length === Object.keys(allSegments).length
    ) {
        validSegments = {start: {}, end: {}};
        setValidSegments(validSegments);
        setPlaceholderDate(
            createPlaceholderRangeValue({
                placeholderValue: props.placeholderValue,
                timeZone,
            }),
        );
    }

    const {lang} = useLang();
    const displayValue =
        value &&
        value.start.isValid() &&
        value.end.isValid() &&
        Object.keys(validSegments.start).length >= Object.keys(allSegments).length &&
        Object.keys(validSegments.end).length >= Object.keys(allSegments).length
            ? {
                  start: value.start.timeZone(timeZone).locale(lang),
                  end: value.end.timeZone(timeZone).locale(lang),
              }
            : {
                  start: placeholderDate.start.timeZone(timeZone).locale(lang),
                  end: placeholderDate.end.timeZone(timeZone).locale(lang),
              };
    const sectionsState = useSectionsState(sections, displayValue, validSegments, delimiter);

    const [selectedSections, setSelectedSections] = React.useState<number | 'all'>(-1);

    const selectedSectionIndexes = React.useMemo<{
        startIndex: number;
        endIndex: number;
    } | null>(() => {
        if (selectedSections === -1) {
            return null;
        }

        if (selectedSections === 'all') {
            return {
                startIndex: 0,
                endIndex: sectionsState.editableSections.length - 1,
            };
        }

        if (typeof selectedSections === 'number') {
            return {startIndex: selectedSections, endIndex: selectedSections};
        }

        if (typeof selectedSections === 'string') {
            const selectedSectionIndex = sectionsState.editableSections.findIndex(
                (section) => section.type === selectedSections,
            );

            return {startIndex: selectedSectionIndex, endIndex: selectedSectionIndex};
        }

        return selectedSections;
    }, [selectedSections, sectionsState.editableSections]);

    function setValue(newValue: RangeValue<DateTime>) {
        if (props.disabled || props.readOnly) {
            return;
        }

        if (
            isAllSegmentsValid(allSegments, validSegments.start) &&
            isAllSegmentsValid(allSegments, validSegments.end)
        ) {
            if (!value || !(newValue.start.isSame(value.start) && newValue.end.isSame(value.end))) {
                handleUpdateRange({
                    start: adjustDateToFormat(newValue.start, formatInfo, 'startOf'),
                    end: adjustDateToFormat(newValue.end, formatInfo, 'endOf'),
                });
            }
        } else {
            if (value) {
                handleUpdateRange(null);
            }
            setPlaceholderDate(newValue);
        }
    }

    function markValid(portion: 'start' | 'end', part: DateFieldSectionType) {
        validSegments[portion] = markValidSection(allSegments, validSegments[portion], part);
        setValidSegments({...validSegments, [portion]: {...validSegments[portion]}});
    }

    function setSection(sectionIndex: number, amount: number) {
        const portion = sectionIndex <= sections.length ? 'start' : 'end';
        const section = sectionsState.editableSections[sectionIndex];
        markValid(portion, section.type);
        setValue({
            ...displayValue,
            [portion]: setSegment(section, displayValue[portion], amount),
        });
    }

    function adjustSection(sectionIndex: number, amount: number) {
        const section = sectionsState.editableSections[sectionIndex];
        const portion = sectionIndex <= sections.length ? 'start' : 'end';
        if (validSegments[portion][section.type]) {
            setValue({
                ...displayValue,
                [portion]: addSegment(section, displayValue[portion], amount),
            });
        } else {
            markValid(portion, section.type);
            if (Object.keys(validSegments[portion]).length >= Object.keys(allSegments).length) {
                setValue(displayValue);
            }
        }
    }

    function flushValidSection(sectionIndex: number) {
        const portion = sectionIndex <= sections.length ? 'start' : 'end';
        delete validSegments[portion][sectionsState.editableSections[sectionIndex].type];
        setValidSegments({...validSegments, [portion]: {...validSegments[portion]}});
    }

    function flushAllValidSections() {
        validSegments = {start: {}, end: {}};
        setValidSegments({start: {}, end: {}});
    }

    function getSectionValue(sectionIndex: number) {
        const portion = sectionIndex <= sections.length ? 'start' : 'end';
        return displayValue[portion];
    }

    function setSectionValue(sectionIndex: number, currentValue: DateTime) {
        const portion = sectionIndex <= sections.length ? 'start' : 'end';
        setValue({...displayValue, [portion]: currentValue});
    }

    function createPlaceholder() {
        return createPlaceholderRangeValue({
            placeholderValue: props.placeholderValue,
            timeZone,
        });
    }

    function setValueFromString(str: string) {
        const [startDate = '', endDate = ''] = str.split(delimiter);
        const parseDate = props.parseDateFromString ?? parseDateFromString;
        const start = parseDate(startDate, format, timeZone);
        const end = parseDate(endDate, format, timeZone);
        if (start.isValid() && end.isValid()) {
            handleUpdateRange({start, end});
            return true;
        }
        return false;
    }

    const validationState =
        props.validationState ||
        (isInvalid(value?.start, props.minValue, props.maxValue) ? 'invalid' : undefined) ||
        (isInvalid(value?.end, props.minValue, props.maxValue) ? 'invalid' : undefined) ||
        (value && !isValidRange(value) ? 'invalid' : undefined) ||
        (value && props.isDateUnavailable?.(value.start) ? 'invalid' : undefined) ||
        (value && props.isDateUnavailable?.(value.end) ? 'invalid' : undefined);

    return useBaseDateFieldState({
        value,
        displayValue,
        placeholderValue: props.placeholderValue,
        timeZone,
        validationState,
        editableSections: sectionsState.editableSections,
        formatInfo,
        readOnly: props.readOnly,
        disabled: props.disabled,
        selectedSectionIndexes,
        selectedSections,
        isEmpty:
            Object.keys(validSegments.start).length === 0 &&
            Object.keys(validSegments.end).length === 0,
        flushAllValidSections,
        flushValidSection,
        setSelectedSections,
        setValue,
        setDate: handleUpdateRange,
        adjustSection,
        setSection,
        getSectionValue,
        setSectionValue,
        createPlaceholder,
        setValueFromString,
    });
}

function useSectionsState(
    sections: DateFieldSectionWithoutPosition[],
    value: RangeValue<DateTime>,
    validSegments: RangeValue<typeof EDITABLE_SEGMENTS>,
    delimiter: string,
) {
    const [state, setState] = React.useState(() => {
        return {
            value,
            sections,
            validSegments,
            delimiter,
            editableSections: getRangeEditableSections(sections, value, validSegments, delimiter),
        };
    });

    if (
        sections !== state.sections ||
        validSegments !== state.validSegments ||
        !(value.start.isSame(state.value.start) && value.end.isSame(state.value.end)) ||
        value.start.timeZone() !== state.value.start.timeZone() ||
        value.start.locale() !== state.value.start.locale() ||
        delimiter !== state.delimiter
    ) {
        setState({
            value,
            sections,
            validSegments,
            delimiter,
            editableSections: getRangeEditableSections(sections, value, validSegments, delimiter),
        });
    }

    return state;
}
